import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { Form, Formik, useFormikContext } from "formik";
import { InputControl } from "formik-chakra-ui";
import React, { useCallback } from "react";
import { CustomSelect } from "src/components/Inputs/CustomSelect";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import FormikLexicalEditor from "src/components/WysiwygEditor/lexical/FormikEditor";
import { FORM_STATUS } from "src/constants";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { validateWithZod } from "src/services/formValidations";
import { stringsAsEnums } from "src/types/enum";
import * as AF from "src/types/formTemplate";
import { SectionTypeList } from "src/types/formTemplate/v2";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { generateKey, isUserGeneratedKey } from "../../autoGenerateKey";
import { useEditFormTemplateContext } from "../../context";
import { DirtyFormTracker } from "../QuestionForm/DirtyFormTracker";
import { PermissionLevel } from "./PermissionLevel";

type SectionCategoryItem = { key: string; label: string };

const z_number = z.string().superRefine((value, ctx) => {
  const numberValue = Number(value);
  if (isNaN(numberValue)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Must contain only number",
    });
  } else if (numberValue < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Must be a number greater than or equal to 0",
    });
  } else if (!Number.isInteger(numberValue)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Must be a whole number",
    });
  }
});

const FamilyEditableStatusSchema = z.object({
  id: z.nativeEnum(GQL.form_status_enum),
  label: z.string(),
});

type FamilyEditableStatusType = z.infer<typeof FamilyEditableStatusSchema>;

const FAMILY_EDITABLE_STATUSES: FamilyEditableStatusType[] = Object.entries(
  FORM_STATUS
)
  .filter(
    ([key]) =>
      ![
        GQL.form_status_enum.Deleted,
        GQL.form_status_enum.Cancelled,
        GQL.form_status_enum.Withdrawn,
      ].includes(key as GQL.form_status_enum)
  )
  .map(([key, status]) => ({
    id: key as GQL.form_status_enum,
    label: status.label,
  }));

const DEFAULT_FAMILY_EDITABLE_STATUSES: FamilyEditableStatusType[] = [
  {
    id: GQL.form_status_enum.InProgress,
    label: FORM_STATUS[GQL.form_status_enum.InProgress].label,
  },
];

const SectionFormSchema = z
  .object({
    id: z.string(),
    title: z.string().trim().min(1, "Section title is required"),
    key: z.string().trim().min(1, "Section key is required"),
    userGeneratedKey: z.boolean(),
    type: z.string().min(1, "Section category is required"),
    description: z.string().optional(),
    permissionLevelSwitch: z.boolean(),
    permissionLevel: z.string(),
    minSchools: z_number,
    maxSchools: z_number,
    exploreUrl: z.string().optional(),
    rankingEnabled: z.boolean().optional().default(true),
    familyEditableStatuses: z.array(FamilyEditableStatusSchema).nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.permissionLevelSwitch && !value.permissionLevel) {
      ctx.addIssue({
        path: ["permissionLevel"],
        code: z.ZodIssueCode.custom,
        message: "Permission level is required when internal-only is selected",
      });
    }
  });

export type SectionFormType = z.infer<typeof SectionFormSchema>;
type EditProps = {
  isOpen: boolean;
  section?: AF.V2.Section;
  onCancelButtonClick: () => void;
  onCreate: (section: SectionFormType) => void;
  applicableSectionTypes?: string[];
};

const FamilyEditableStatusesSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<SectionFormType>();

  const renderFamilyEditableStatusDropdown = useCallback(
    (item: FamilyEditableStatusType) => {
      return <Box>{item.label}</Box>;
    },
    []
  );

  return (
    <Flex alignItems="start" mt="0.5rem" mb="1.5rem">
      <Flex direction="column">
        <Text>Family edit by status</Text>
        <Text fontSize="xs" color="gray.600" fontWeight="400">
          Allow Families to edit only during certain statuses. You may select
          multiple. Leave this blank to fully prevent editing.
        </Text>
        <FormControl id="familyEditableStatuses" w="100%" mt={2}>
          <FormLabel htmlFor="familyEditableStatuses" mb={0}>
            Can edit during
          </FormLabel>
          <MultiSelectInput<FamilyEditableStatusType>
            validateSelectedItems
            size="md"
            options={FAMILY_EDITABLE_STATUSES}
            initialValues={values.familyEditableStatuses ?? []}
            onChange={(selectedStatuses) => {
              setFieldValue("familyEditableStatuses", selectedStatuses);
            }}
            renderDropdownItem={renderFamilyEditableStatusDropdown}
          />
          {values.type ===
            GQL.form_template_section_type_enum.SchoolRankingSection && (
            <Text fontSize="sm" color="gray.600" fontWeight="400" mt={2}>
              The school ranking section also enables editing school specific
              questions in any following section.
            </Text>
          )}
        </FormControl>
      </Flex>
    </Flex>
  );
};

export const SectionFormDialog: React.FC<EditProps> = ({
  isOpen,
  section,
  onCancelButtonClick,
  onCreate,
}) => {
  const schoolSection =
    section?.type === AF.V2.SectionTypes.SchoolRankingSection
      ? (section as AF.V2.SchoolRankingSection)
      : null;

  const userGeneratedKey = isUserGeneratedKey(section?.key, section?.title);

  /** Need to memoize this so we don't trigger formik reinitialization unnecessarily */
  const initialValues = React.useMemo(
    () =>
      stringsAsEnums<SectionFormType>({
        id: section?.id ?? crypto.randomUUID(), // TODO: LOI - may move to backend
        type: section?.type ?? "",
        title: section?.title ?? "",
        description: section?.description ?? "",
        key: section?.key ?? "",
        userGeneratedKey,
        permissionLevelSwitch: section?.permissionLevel ? true : false,
        permissionLevel: section?.permissionLevel ?? "",
        minSchools: schoolSection?.minSchools
          ? schoolSection.minSchools.toString()
          : "",
        maxSchools: schoolSection?.maxSchools
          ? schoolSection.maxSchools.toString()
          : "",
        exploreUrl: schoolSection?.exploreUrl ?? "",
        rankingEnabled: schoolSection?.rankingEnabled ?? true,
        familyEditableStatuses:
          section?.familyEditableStatuses?.map((status) => ({
            id: status,
            label:
              FAMILY_EDITABLE_STATUSES.find((item) => item.id === status)
                ?.label ?? "",
          })) ?? DEFAULT_FAMILY_EDITABLE_STATUSES,
      }),
    [section, schoolSection, userGeneratedKey]
  );

  const validateFormikValues = async (values: SectionFormType) => {
    const errors = await validateWithZod(SectionFormSchema)(values);

    if (errors) return errors;

    const { minSchools, maxSchools } = values;

    if (minSchools && maxSchools && Number(minSchools) > Number(maxSchools))
      return {
        minSchools: "Min schools must not exceed max schools.",
        maxSchools: "Max schools must not be less than min schools.",
      };
  };

  const sectionTypeLabels = {
    DisclaimerSection: "Disclaimer",
    GeneralSection: "General",
    PreRankingSection: "Pre-ranking",
    SchoolRankingSection: "School ranking",
  };

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Change section category",
    body: "This section contains questions that will be removed if you change to this category. Are you sure you want to continue?",
    confirmButton: {
      label: "Yes, change category",
    },
  });

  const context = useEditFormTemplateContext();
  const isUnpublishedSection =
    !section || context.helpers.isUnpublishedSection(section.id);

  const sectionCategories: SectionCategoryItem[] = SectionTypeList.map(
    (value) => ({
      label: sectionTypeLabels[value as AF.V2.SectionType],
      key: value,
    })
  );

  return (
    <>
      <Formik<SectionFormType>
        initialValues={initialValues}
        enableReinitialize
        validateOnBlur={false}
        onSubmit={onCreate}
        validate={validateFormikValues}
      >
        {(formik) => {
          const onCancel = () => {
            formik.handleReset();
            onCancelButtonClick();
          };

          return (
            <Modal
              isOpen={isOpen}
              onClose={onCancel}
              scrollBehavior="outside"
              isCentered={true}
            >
              <ModalOverlay />
              <ModalContent as={Form}>
                <ModalHeader>
                  {section ? "Edit section" : "Add section"}
                </ModalHeader>
                <ModalBody as={Flex} direction="column" gap={2}>
                  <DirtyFormTracker />
                  <InputControl
                    label="Section title"
                    id="title"
                    name="title"
                    isRequired
                    inputProps={{
                      onChange: async (event) => {
                        const title = event.target.value;

                        await formik.setFieldValue("title", title);
                        if (
                          isUnpublishedSection &&
                          !formik.values.userGeneratedKey
                        ) {
                          await formik.setFieldValue("key", generateKey(title));
                        }
                      },
                    }}
                  />
                  <InputControl
                    label="Section key"
                    id="key"
                    name="key"
                    isRequired
                    inputProps={{
                      onChange: async (event) => {
                        const key = event.target.value;
                        await formik.setFieldValue("key", key);
                        await formik.setFieldValue(
                          "userGeneratedKey",
                          key.trim().length > 0
                        );
                      },
                    }}
                  />

                  <CustomSelect<SectionCategoryItem>
                    label="Section category"
                    items={sectionCategories}
                    renderItem={(item) => item.label}
                    renderSelectedItem={(item) => item?.label ?? ""}
                    disabled={!isUnpublishedSection}
                    isRequired
                    error={
                      formik.touched["type"] ? formik.errors["type"] : undefined
                    }
                    onChange={async (item) => {
                      const value = item?.key;
                      const hasQuestions =
                        section !== undefined &&
                        "questions" in section &&
                        section.questions?.length > 0;

                      const sectionTypeWithoutQuestions = [
                        GQL.form_template_section_type_enum.DisclaimerSection,
                        GQL.form_template_section_type_enum
                          .SchoolRankingSection,
                      ].includes(value as GQL.form_template_section_type_enum);

                      if (
                        hasQuestions &&
                        sectionTypeWithoutQuestions &&
                        !(await confirm())
                      ) {
                        return;
                      }

                      await formik.setFieldValue("type", value);
                    }}
                    selectedItem={
                      sectionCategories.find(
                        (i) => i.key === formik.values.type
                      ) ?? null
                    }
                  />
                  <FormikLexicalEditor
                    format="html"
                    label="Section description"
                    name="description"
                    height="5rem"
                    settings={{
                      enableImage: false,
                      enableBlockFormat: false,
                      enableElementFormat: false,
                      enableCode: false,
                      enableHorizontalRule: false,
                      enableFontColor: true,
                      enableBackgroundColor: true,
                    }}
                  />

                  {formik.values.type ===
                    GQL.form_template_section_type_enum
                      .SchoolRankingSection && (
                    <>
                      <InputControl
                        label="Explore Link URL"
                        id="exploreUrl"
                        name="exploreUrl"
                        inputProps={{
                          placeholder:
                            "E.g.: https://springfield.explore.avela.org",
                        }}
                      />
                      <HStack>
                        <InputControl
                          label="Min. schools (optional)"
                          id="minSchools"
                          name="minSchools"
                          inputProps={{ placeholder: "E.g.: 3" }}
                        />
                        <InputControl
                          label="Max. schools (optional)"
                          id="maxSchools"
                          name="maxSchools"
                          inputProps={{ placeholder: "E.g.: 7" }}
                        />
                      </HStack>
                    </>
                  )}
                  {formik.values.type !==
                    GQL.form_template_section_type_enum.PreRankingSection &&
                  formik.values.type !==
                    GQL.form_template_section_type_enum.SchoolRankingSection ? (
                    <PermissionLevel name="permissionLevel" />
                  ) : null}
                  {formik.values.type ===
                    GQL.form_template_section_type_enum
                      .SchoolRankingSection && (
                    <>
                      <Flex alignItems="start" mb="1.5rem">
                        <FormikSwitchInput<boolean | null>
                          label=""
                          name="rankingEnabled"
                          checked={[true, ""]}
                          unchecked={[false, ""]}
                          containerProps={{ direction: "row", gap: "0.5rem" }}
                        />
                        <Flex direction="column">
                          <Text>Ordered ranking</Text>
                          <Text fontSize="xs" color="gray.600" fontWeight="400">
                            When enabled, choices will have an order of
                            preference
                          </Text>
                        </Flex>
                      </Flex>
                    </>
                  )}

                  <Divider />
                  <FamilyEditableStatusesSection />
                </ModalBody>
                <ModalFooter gap="1.0rem">
                  <Button variant="ghost" colorScheme="gray" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {section ? "Update" : "Add"} section
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          );
        }}
      </Formik>
      {confirmationDialog}
    </>
  );
};
